import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { DomainOperation, DomainTld } from '../types/models/DomainPricing';

interface DomainMatrixTableProps {
  operations: DomainOperation[];
  /** Row axis — the priceable (active) TLDs. */
  tlds: DomainTld[];
  ariaLabel: string;
  /** Renders one price cell's content (the editor or read-only value). */
  renderCell: (operation: DomainOperation, tld: DomainTld) => React.ReactNode;
  /** Extra toolbar controls (e.g. a Refresh button), shown next to the search field. */
  toolbarActions?: React.ReactNode;
  /** Shown when there are no TLDs at all (distinct from the "no search match" state). */
  emptyState: React.ReactNode;
}

// The (TLD × operation) matrix shell: a sticky-first-column table inside a horizontal scroll
// container, with search-by-TLD + pagination. The price cells themselves are delegated to renderCell
// so the same table serves both the default-price tab and the per-client overrides tab.
function DomainMatrixTable({ operations, tlds, ariaLabel, renderCell, toolbarActions, emptyState }: DomainMatrixTableProps) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce the search so filtering doesn't run on every keystroke (CLAUDE.md: 400ms).
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filteredTlds = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return tlds;
    }
    return tlds.filter((t) => t.tld.toLowerCase().includes(q) || t.label.toLowerCase().includes(q));
  }, [tlds, debouncedSearch]);

  const paginatedTlds = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTlds.slice(start, start + perPage);
  }, [filteredTlds, page, perPage]);

  if (tlds.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by TLD..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          {toolbarActions && <ToolbarItem>{toolbarActions}</ToolbarItem>}
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={filteredTlds.length}
              perPage={perPage}
              page={page}
              onSetPage={(_event, newPage) => setPage(newPage)}
              onPerPageSelect={(_event, newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      {filteredTlds.length === 0 ? (
        <EmptyState titleText="No TLDs match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term, or clear the filter to see all TLDs.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setSearchValue('')}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <InnerScrollContainer>
          <Table aria-label={ariaLabel} variant="compact" gridBreakPoint="">
            <Thead>
              <Tr>
                <Th isStickyColumn hasRightBorder modifier="nowrap" stickyMinWidth="160px">
                  TLD
                </Th>
                {operations.map((op) => (
                  <Th key={op.slug} modifier="nowrap">
                    {op.label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {paginatedTlds.map((tld) => (
                <Tr key={tld.tld}>
                  <Th isStickyColumn hasRightBorder modifier="nowrap" stickyMinWidth="160px">
                    {tld.label}
                  </Th>
                  {operations.map((op) => (
                    <Td key={op.slug} modifier="nowrap" dataLabel={op.label}>
                      {renderCell(op, tld)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </InnerScrollContainer>
      )}
      {filteredTlds.length > 0 && (
        <Pagination
          itemCount={filteredTlds.length}
          perPage={perPage}
          page={page}
          onSetPage={(_event, newPage) => setPage(newPage)}
          onPerPageSelect={(_event, newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          variant="bottom"
        />
      )}
    </>
  );
}

export default DomainMatrixTable;
